/** @jsxImportSource @emotion/react */
import 'twin.macro';

import { css } from '@emotion/react';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { useClickAway, useMount, useToggle } from 'react-use';
import { SimpleMenuSurface } from '@rmwc/menu';
import { Ripple } from '@rmwc/ripple';
import { CollapsibleList } from '@rmwc/list';
import { ReactComponent as FilterSvg } from 'assets/Icons/filter.svg';
import { ReactComponent as CollapsedSvg } from 'assets/Icons/chevron_right.svg';
import { ReactComponent as ExpandedSvg } from 'assets/Icons/expand_more.svg';
import { SimpleCheckbox } from 'components/shared/SimpleCheckbox';
import { scrollbarWidthCss } from 'components/shared/twin.styles';
import { ListOption } from 'utils/models';
import tw from 'twin.macro';

type ContentTypeFilter = 'audio' | 'video';
type FileSizeFilter = '0-50' | '50-200' | '200-500' | '500+';
type DurationFilter = 'UNDER_10' | '10_30' | '30_60' | 'OVER_60';

type FilterState = {
  contentTypes: ContentTypeFilter[];
  fileSizes: FileSizeFilter[];
  durations: DurationFilter[];
  computeTimes: DurationFilter[];
};

type FilterOption<T extends string> = {
  id: T;
  label: string;
};

type RangeFilterOption<T extends string> = FilterOption<T> & {
  min: number;
  max?: number;
  includeMin?: boolean;
  includeMax?: boolean;
};

const CONTENT_TYPE_FILTERS: FilterOption<ContentTypeFilter>[] = [
  { id: 'audio', label: 'Audio' },
  { id: 'video', label: 'Video' },
];

const FILE_SIZE_FILTERS: RangeFilterOption<FileSizeFilter>[] = [
  { id: '0-50', label: '0–50 MB', min: 0, max: 50, includeMin: true, includeMax: true },
  { id: '50-200', label: '50–200 MB', min: 50, max: 200, includeMin: false, includeMax: true },
  { id: '200-500', label: '200–500 MB', min: 200, max: 500, includeMin: false, includeMax: true },
  { id: '500+', label: '500 MB+', min: 500, includeMin: false },
];

const DURATION_FILTERS: RangeFilterOption<DurationFilter>[] = [
  { id: 'UNDER_10', label: 'Under 10 minutes', min: 0, max: 10, includeMin: true, includeMax: false },
  { id: '10_30', label: '10–30 minutes', min: 10, max: 30, includeMin: true, includeMax: true },
  { id: '30_60', label: '30–60 minutes', min: 30, max: 60, includeMin: false, includeMax: true },
  { id: 'OVER_60', label: 'Over 60 minutes', min: 60, includeMin: false },
];

const COMPUTE_TIME_FILTERS = DURATION_FILTERS;

// Convert filter options to ListOption format for FilterCollapsibleItem
const contentTypeOptions: ListOption[] = CONTENT_TYPE_FILTERS.map((option) => ({
  label: option.label,
  value: option.id,
}));

const fileSizeOptions: ListOption[] = FILE_SIZE_FILTERS.map((option) => ({
  label: option.label,
  value: option.id,
}));

const durationOptions: ListOption[] = DURATION_FILTERS.map((option) => ({
  label: option.label,
  value: option.id,
}));

const computeTimeOptions: ListOption[] = COMPUTE_TIME_FILTERS.map((option) => ({
  label: option.label,
  value: option.id,
}));

// Export types and constants for use in parent component
export type { ContentTypeFilter, FileSizeFilter, DurationFilter, FilterState };
export { CONTENT_TYPE_FILTERS, FILE_SIZE_FILTERS, DURATION_FILTERS, COMPUTE_TIME_FILTERS };
export type { FilterOption, RangeFilterOption };

interface FiltersButtonProps {
  filters: FilterState;
  activeFilterCount: number;
  isFilterOpen: boolean;
  onToggleFilterOpen: () => void;
  onToggleFilterValue: <T extends keyof FilterState>(
    key: T,
    option: FilterState[T][number],
  ) => void;
  onClearFilters: () => void;
}

// Simple filter item without search (for Content Type, Duration, Compute Time)
const SimpleFilterCollapsibleItem = ({
  title,
  optionList,
  defaultSelectedIds,
  onSelectionChanged,
  defaultOpen = false,
}: {
  title: string;
  optionList: ListOption[];
  defaultSelectedIds: string[];
  onSelectionChanged: (selectedIds: string[]) => void;
  defaultOpen?: boolean;
}) => {
  const [isExpanded, toggleExpanded] = useToggle(defaultOpen);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedIds(defaultSelectedIds);
  }, [defaultSelectedIds]);

  useMount(() => {
    if (defaultSelectedIds.length > 0) {
      onSelectionChanged(defaultSelectedIds);
    }
  });

  const handleToggleSelect = (item: ListOption) => {
    const isExisted = selectedIds.includes(item.value);
    const newSelectedIds = isExisted
      ? selectedIds.filter((value) => value !== item.value)
      : selectedIds.concat(item.value);

    setSelectedIds(newSelectedIds);
    onSelectionChanged(newSelectedIds);
  };

  return (
    <div
      tw="bg-[#f9f9f9] rounded mb-2 z-[32]"
      onClick={(e) => e.stopPropagation()}
    >
      <CollapsibleList
        handle={
          <Ripple tw="w-full bg-white rounded" onClick={toggleExpanded}>
            <div tw="flex items-center py-1 cursor-pointer [svg]:(fill[#7f8090])">
              <span tw="flex">
                {isExpanded ? <ExpandedSvg /> : <CollapsedSvg />}
              </span>
              <div tw="flex flex-nowrap text-14 font-medium text-sonnant-grey-6 opacity-95 select-none">
                {title}
              </div>
            </div>
          </Ripple>
        }
        open={isExpanded}
      >
        {isExpanded && (
          <div tw="flex flex-col gap-y-2 px-4 py-4 pb-2">
            <div tw="flex flex-col pl-1">
              {optionList.map((item, index) => (
                <div
                  key={index}
                  tw="flex justify-start items-center flex-row w-full select-none [label]:(font-medium)"
                  title={item.label}
                >
                  <SimpleCheckbox
                    checked={selectedIds.includes(item.value)}
                    onChange={() => handleToggleSelect(item)}
                    label={item.label}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </CollapsibleList>
    </div>
  );
};

const UsageTrackingFilterButton = ({
  filtersCount,
  onClick,
}: {
  filtersCount: number;
  onClick: () => void;
}) => {
  return (
    <button
      className="button large btn-primary"
      tw="(mb-0 flex justify-center items-center gap-x-2.5 shadow w-[unset] min-w-[unset] pl-[1rem] pr-[1.2rem])!"
      onClick={onClick}
    >
      <span tw="flex">
        <FilterSvg />
      </span>

      <span>Filters</span>

      <div tw="flex items-center justify-center ml-1.5">
        <Ripple>
          <div tw="min-w-[2.2rem] h-[2.2rem] shadow-sm rounded-full flex justify-center items-center bg-white text-sonnant-purple-3">
            <span tw="px-1.5">{filtersCount}</span>
          </div>
        </Ripple>
      </div>
    </button>
  );
};

export const FiltersButton = ({
  filters,
  activeFilterCount,
  isFilterOpen,
  onToggleFilterOpen,
  onToggleFilterValue,
  onClearFilters,
}: FiltersButtonProps) => {
  const filterWrapperRef = useRef<HTMLDivElement>(null);

  useClickAway(filterWrapperRef, () => {
    if (!isFilterOpen) return;

    const timer = setTimeout(() => {
      onToggleFilterOpen();
      clearTimeout(timer);
    }, 100);
  });

  const handleContentTypeChange = (selectedIds: string[]) => {
    // Sync with parent: toggle items that changed
    const currentIds = filters.contentTypes;
    const newIds = selectedIds as ContentTypeFilter[];
    
    // Add new selections
    newIds.forEach((id) => {
      if (!currentIds.includes(id)) {
        onToggleFilterValue('contentTypes', id);
      }
    });
    
    // Remove deselections
    currentIds.forEach((id) => {
      if (!newIds.includes(id)) {
        onToggleFilterValue('contentTypes', id);
      }
    });
  };

  const handleFileSizeChange = (selectedIds: string[]) => {
    const currentIds = filters.fileSizes;
    const newIds = selectedIds as FileSizeFilter[];
    
    newIds.forEach((id) => {
      if (!currentIds.includes(id)) {
        onToggleFilterValue('fileSizes', id);
      }
    });
    
    currentIds.forEach((id) => {
      if (!newIds.includes(id)) {
        onToggleFilterValue('fileSizes', id);
      }
    });
  };

  const handleDurationChange = (selectedIds: string[]) => {
    const currentIds = filters.durations;
    const newIds = selectedIds as DurationFilter[];
    
    newIds.forEach((id) => {
      if (!currentIds.includes(id)) {
        onToggleFilterValue('durations', id);
      }
    });
    
    currentIds.forEach((id) => {
      if (!newIds.includes(id)) {
        onToggleFilterValue('durations', id);
      }
    });
  };

  const handleComputeTimeChange = (selectedIds: string[]) => {
    const currentIds = filters.computeTimes;
    const newIds = selectedIds as DurationFilter[];
    
    newIds.forEach((id) => {
      if (!currentIds.includes(id)) {
        onToggleFilterValue('computeTimes', id);
      }
    });
    
    currentIds.forEach((id) => {
      if (!newIds.includes(id)) {
        onToggleFilterValue('computeTimes', id);
      }
    });
  };

  const handleCloseMenu = () => {
    if (!isFilterOpen) return;
    onToggleFilterOpen();
  };

  const anchorElement: ReactElement = (
    <UsageTrackingFilterButton
      filtersCount={activeFilterCount}
      onClick={onToggleFilterOpen}
    />
  );

  return (
    <div ref={filterWrapperRef} css={wrapperCss} onClick={(e) => e.stopPropagation()}>
      <SimpleMenuSurface
        open={isFilterOpen}
        handle={anchorElement}
        anchorCorner="bottomLeft"
        onClose={handleCloseMenu}
        fixed={false}
        tw="my-[4px] min-w-[28rem] rounded-lg overflow-hidden z-[30]"
      >
        <div tw="px-3 py-3">
          <div tw="text-18 pl-2.5 font-medium opacity-90">Filters</div>

          <div
            tw="max-h-[60vh] overflow-y-auto my-2"
            css={[scrollbarWidthCss(4)]}
          >
            <SimpleFilterCollapsibleItem
              title="Content Type"
              optionList={contentTypeOptions}
              defaultSelectedIds={filters.contentTypes}
              onSelectionChanged={handleContentTypeChange}
              defaultOpen
            />

            <SimpleFilterCollapsibleItem
              title="File Size"
              optionList={fileSizeOptions}
              defaultSelectedIds={filters.fileSizes}
              onSelectionChanged={handleFileSizeChange}
            />

            <SimpleFilterCollapsibleItem
              title="Transcribed Duration"
              optionList={durationOptions}
              defaultSelectedIds={filters.durations}
              onSelectionChanged={handleDurationChange}
            />

            <SimpleFilterCollapsibleItem
              title="Compute Time"
              optionList={computeTimeOptions}
              defaultSelectedIds={filters.computeTimes}
              onSelectionChanged={handleComputeTimeChange}
            />
          </div>

          <div tw="flex gap-x-3 justify-end [button]:(text-13)!">
            {activeFilterCount > 0 && (
              <button
                className="button btn-primary medium"
                tw="(px-[1rem] mb-0 bg-sonnant-red)!"
                onClick={onClearFilters}
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </SimpleMenuSurface>
    </div>
  );
};

const wrapperCss = css`
  .mdc-menu-surface {
    ${tw`max-h-[unset]!`}
  }
`;

